import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Grid, Card, CardContent, Typography, Box, Button, CardActionArea } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import api from '../services/api';

type Offer = {
  id: number;
  slug: string;
  title_en: string;
  title_pl?: string;
  status: 'draft'|'open'|'paused'|'closed';
  position_title_en?: string;
  position_title_pl?: string;
  description_md?: string;
  salary?: string;
  work_place?: string;
};

const CareersPage: React.FC = () => {
  const [offers, setOffers] = React.useState<Offer[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  // uproszczony stan – przyciski dev zostały usunięte

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await api.get('/recruit/public/offers');
        if (!mounted) return;
        setOffers(resp.data?.data || []);
      } catch (e) {
        setOffers([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // brak dodatkowych akcji na tej stronie

  return (
    <Container maxWidth="lg" sx={{ py: 6, bgcolor: '#FAFAFA' }}>
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
            fontWeight: 600,
            fontSize: '21.94px',
            lineHeight: '26px',
            letterSpacing: '0.12em',
            color: 'rgba(197, 48, 48, 1)',
            width: '140px',
            height: '26px',
            display: 'inline-block'
          }}
        >
          UNITRESCO
        </Typography>
      </Box>
      {/* Tytuł strony */}
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
          fontWeight: 600,
          fontSize: '48px',
          lineHeight: '100%',
          letterSpacing: 0,
          color: 'rgba(2, 2, 66, 1)',
          mt: '60px',
          mb: '25px'
        }}
      >
        Kariera
      </Typography>
      {/* Lead */}
      <Typography sx={{
        fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
        fontWeight: 400,
        fontSize: '24px',
        lineHeight: '100%',
        color: 'rgba(2, 2, 66, 1)',
        mb: '15px'
      }}>
        Zatrudniamy ludzi, inwestujemy w rozwój, dbamy o bezpieczeństwo.
      </Typography>
      {/* Sublead */}
      <Typography sx={{
        fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '100%',
        color: 'rgba(89, 89, 102, 1)',
        mb: '25px'
      }}>
        Szukasz stabilnej pracy w dobrej atmosferze? Sprawdź, kogo aktualnie szukamy.
      </Typography>
      {loading ? (
        <Typography>Ładowanie…</Typography>
      ) : (
        <Grid container spacing={3}>
          {offers.map((o) => (
            <Grid item xs={12} md={6} lg={4} key={o.id} sx={{ display: 'flex' }}>
              <Card
                variant="outlined"
                sx={{
                  width: '384px',
                  minHeight: '210px',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '4px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'rgba(212, 212, 217, 1)',
                  padding: '20px',
                  gap: '40px',
                  transition: 'border-color .2s ease, box-shadow .2s ease',
                  '&:hover': {
                    borderColor: 'rgba(2, 2, 66, 1)',
                    boxShadow: '0 0 0 1px rgba(2, 2, 66, 0.6)'
                  }
                }}
              >
                {/* Klikalny obszar kafelka */}
                <CardActionArea
                  component="div"
                  disableRipple
                  disableTouchRipple
                  onClick={() => navigate(`/careers/${o.slug}`)}
                  sx={{
                    borderRadius: '4px',
                    cursor: 'pointer',
                    outline: 'none',
                    backgroundColor: 'transparent !important',
                    '&:hover': { backgroundColor: 'transparent !important' },
                    '&:focus, &:active': { backgroundColor: 'transparent', outline: 'none' },
                    '& .MuiCardActionArea-focusHighlight': { opacity: 0, backgroundColor: 'transparent' }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Typography
                    gutterBottom
                    sx={{
                      fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '100%',
                      color: 'rgba(2, 2, 66, 1)'
                    }}
                  >
                    {o.title_pl || o.title_en}
                  </Typography>
                  {(o.work_place || o.position_title_pl || o.position_title_en) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">{o.work_place || o.position_title_pl || o.position_title_en}</Typography>
                    </Box>
                  )}
                  {o.salary && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{
                        fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '100%',
                        color: 'rgba(89, 89, 102, 1)'
                      }}>{o.salary}</Typography>
                      <Typography sx={{
                        fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '100%',
                        letterSpacing: 0,
                        color: 'rgba(89, 89, 102, 1)',
                        mt: '7px'
                      }}>brutto miesięcznie</Typography>
                    </Box>
                  )}
                  </CardContent>
                </CardActionArea>
                <Box sx={{ pt: 0 }}>
                  <Button
                    onClick={() => navigate(`/careers/${o.slug}`)}
                    sx={{
                      textTransform: 'none',
                      fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '100%',
                      borderRadius: '4px',
                      padding: '12px 16px',
                      color: 'rgba(255, 255, 255, 1)',
                      backgroundColor: 'rgba(2, 2, 66, 1)',
                      transition: 'background-color .15s ease',
                      '&:hover': { backgroundColor: 'rgba(2, 2, 66, 0.78)' }
                    }}
                  >
                    Szczegóły oferty
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
          {!offers.length && (
            <Grid item xs={12}>
              <Typography>Brak otwartych ofert.</Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default CareersPage;


